import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Users, FileText, BarChart, Calendar, MessageSquare, Award } from 'lucide-react';

const HRCopilotCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Base64 encoded images
  const professionalImage = "images/hr.png";
  const blueShapeImage = "https://framerusercontent.com/images/K67zqxljSwqr3qyboWbs4TwnLME.png?scale-down-to=1024&width=1793&height=2000";

  const slides = [
    {
      title: "AI-Powered HR Solutions",
      subtitle: "for India's boldest disruptors",
      features: "Smart Recruiting | Employee Management | Analytics Dashboard",
      buttons: [
        { text: "Sign Up Now", primary: true },
        { text: "Know More", primary: false }
      ],
      founderName: "Priya Sharma",
      founderTitle: "HRCOPILOT FOUNDER",
      founderImage: professionalImage,
      productName: "HR Copilot",
      productPrice: "₹ 2,999",
      productDesc: "HR Management Suite",
      paymentMethods: ["Card", "UPI ID", "Netbanking", "EMI"],
      actions: [
        { icon: Users, text: "Manage Employees" },
        { icon: FileText, text: "Track Applications" },
        { icon: BarChart, text: "View Analytics" },
        { icon: Calendar, text: "Schedule Interviews" },
        { icon: MessageSquare, text: "Team Communication" },
        { icon: Award, text: "Performance Reviews" }
      ]
    },
    {
      title: "Streamline Your Workforce",
      subtitle: "with intelligent automation",
      features: "Onboarding | Payroll | Performance Tracking",
      buttons: [
        { text: "Get Started", primary: true },
        { text: "Learn More", primary: false }
      ],
      founderName: "Rajesh Kumar",
      founderTitle: "HRCOPILOT CO-FOUNDER",
      founderImage: professionalImage,
      productName: "Talent Hub",
      productPrice: "₹ 4,499",
      productDesc: "Recruitment Platform",
      paymentMethods: ["Card", "UPI ID", "Netbanking", "Wallet"],
      actions: [
        { icon: Users, text: "Post Jobs" },
        { icon: FileText, text: "Screen Resumes" },
        { icon: Calendar, text: "Book Interviews" },
        { icon: BarChart, text: "Track Metrics" },
        { icon: MessageSquare, text: "Chat with Candidates" },
        { icon: Award, text: "Offer Management" }
      ]
    },
    {
      title: "Transform HR Operations",
      subtitle: "with data-driven insights",
      features: "AI Insights | Compliance | Employee Engagement",
      buttons: [
        { text: "Start Free Trial", primary: true },
        { text: "View Demo", primary: false }
      ],
      founderName: "Anjali Reddy",
      founderTitle: "HRCOPILOT CEO",
      founderImage: professionalImage,
      productName: "Analytics Pro",
      productPrice: "₹ 3,499",
      productDesc: "HR Analytics Suite",
      paymentMethods: ["Card", "UPI ID", "Bank Transfer", "EMI"],
      actions: [
        { icon: BarChart, text: "Generate Reports" },
        { icon: Users, text: "Workforce Planning" },
        { icon: Award, text: "Employee Recognition" },
        { icon: Calendar, text: "Leave Management" },
        { icon: FileText, text: "Document Storage" },
        { icon: MessageSquare, text: "Feedback System" }
      ]
    }
  ];

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Auto slide every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 8000);
    return () => clearInterval(interval);
  }, [currentSlide]);

  const currentData = slides[currentSlide];

  return (
    <div className="hr-home-carousel">
      <div className="hr-home-container">
        <button className="hr-home-nav-btn hr-home-nav-left" onClick={prevSlide}>
          <ChevronLeft size={32} />
        </button>

        <div className="hr-home-content-area">
          <div className="hr-home-left-section">
            <h1 className="hr-home-main-title animate-slide-in">
              {currentData.title}
            </h1>
            <h2 className="hr-home-subtitle animate-slide-in-delay">
              {currentData.subtitle}
            </h2>
            <p className="hr-home-features animate-fade-in">
              {currentData.features}
            </p>
            
            <div className="hr-home-button-group">
              {currentData.buttons.map((btn, idx) => (
                <button 
                  key={idx} 
                  className={btn.primary ? "hr-home-btn-primary" : "hr-home-btn-secondary"}
                >
                  {btn.text} {btn.primary && "→"}
                </button>
              ))}
            </div>
          </div>

          <div className="hr-home-right-section">
            <img src={blueShapeImage} alt="" className="hr-home-blue-shape" />
            
            <div className="hr-home-founder-image">
              <img src={currentData.founderImage} alt={currentData.founderName} className="hr-home-founder-photo" />
            </div>

            <div className="hr-home-product-card animate-float">
              <div className="hr-home-product-header">
                <span className="hr-home-powers-text">POWERS</span>
                <span className="hr-home-product-name">{currentData.productName}</span>
              </div>
              
              <div className="hr-home-product-body">
                <div className="hr-home-product-visual"></div>
                <div className="hr-home-product-info">
                  <div className="hr-home-product-desc">{currentData.productDesc}</div>
                  <div className="hr-home-product-price">{currentData.productPrice}</div>
                </div>
              </div>

              <div className="hr-home-payment-section">
                <div className="hr-home-payment-title">Select Payment Method</div>
                <div className="hr-home-payment-grid">
                  {currentData.paymentMethods.map((method, idx) => (
                    <div key={idx} className="hr-home-payment-option">
                      {method}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="hr-home-founder-badge animate-bounce-subtle">
              <span className="hr-home-founder-title">{currentData.founderTitle}</span>
              <span className="hr-home-founder-name">{currentData.founderName}</span>
            </div>
          </div>
        </div>

        <button className="hr-home-nav-btn hr-home-nav-right" onClick={nextSlide}>
          <ChevronRight size={32} />
        </button>
      </div>

      <div className="hr-home-action-bar">
        {currentData.actions.map((action, idx) => {
          const Icon = action.icon;
          return (
            <button key={idx} className="hr-home-action-btn">
              <Icon size={18} />
              <span>{action.text}</span>
            </button>
          );
        })}
      </div>

      <div className="hr-home-dots-indicator">
        {slides.map((_, idx) => (
          <button
            key={idx}
            className={`hr-home-dot ${idx === currentSlide ? 'hr-home-dot-active' : ''}`}
            onClick={() => setCurrentSlide(idx)}
          />
        ))}
      </div>
    </div>
  );
};

export default HRCopilotCarousel;

// Embedded CSS Styles with unique class names
const styles = `
.hr-home-carousel {
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #f0f2ff 0%, #e8ebff 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow: hidden;
}

.hr-home-container {
  position: relative;
  width: 100%;
  max-width: 1400px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 3rem;
}

.hr-home-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  z-index: 10;
  color: #333;
}

.hr-home-nav-btn:hover {
  background: #4169ff;
  color: white;
  box-shadow: 0 6px 20px rgba(65, 105, 255, 0.3);
  transform: translateY(-50%) scale(1.1);
}

.hr-home-nav-left {
  left: -25px;
}

.hr-home-nav-right {
  right: -25px;
}

.hr-home-content-area {
  display: flex;
  gap: 3rem;
  align-items: center;
  min-height: 500px;
}

.hr-home-left-section {
  flex: 1;
  z-index: 2;
}

.hr-home-main-title {
  font-size: 3.5rem;
  font-weight: 700;
  color: #4169ff;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
  opacity: 0;
  animation-fill-mode: forwards;
}

.hr-home-subtitle {
  font-size: 3rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
  line-height: 1.2;
  opacity: 0;
  animation-fill-mode: forwards;
}

.hr-home-features {
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 2.5rem 0;
  opacity: 0;
  animation-fill-mode: forwards;
}

.hr-home-button-group {
  display: flex;
  gap: 1rem;
  opacity: 0;
  animation: hr-home-fade-in 0.8s ease 0.6s forwards;
}

.hr-home-btn-primary {
  background: #4169ff;
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hr-home-btn-primary:hover {
  background: #2d4ed8;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(65, 105, 255, 0.3);
}

.hr-home-btn-secondary {
  background: transparent;
  color: #4169ff;
  border: 2px solid #4169ff;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hr-home-btn-secondary:hover {
  background: #4169ff;
  color: white;
  transform: translateY(-2px);
}

.hr-home-right-section {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;
}

.hr-home-blue-shape {
  position: absolute;
  right: -50px;
  top: 0;
  width: 600px;
  height: 600px;
  object-fit: cover;
  z-index: 1;
  animation: hr-home-rotate 20s linear infinite;
}

.hr-home-founder-image {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: 280px;
  height: 400px;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: hr-home-slide-in-right 0.8s ease-out;
}

.hr-home-founder-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.5s ease;
}

.hr-home-founder-photo:hover {
  transform: scale(1.05);
}

.hr-home-product-card {
  position: absolute;
  right: 20px;
  top: 30px;
  background: #d4ff7f;
  border-radius: 16px;
  padding: 1.5rem;
  width: 320px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  z-index: 4;
  animation: hr-home-slide-in-left 0.8s ease-out;
}

.hr-home-product-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.hr-home-powers-text {
  font-size: 0.75rem;
  color: #666;
  font-weight: 600;
}

.hr-home-product-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
}

.hr-home-product-body {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 1rem;
  align-items: center;
}

.hr-home-product-visual {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #6dd47e 0%, #5ea86f 100%);
  border-radius: 8px;
  animation: hr-home-pulse 2s ease-in-out infinite;
}

.hr-home-product-info {
  flex: 1;
}

.hr-home-product-desc {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.hr-home-product-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
}

.hr-home-payment-section {
  background: white;
  border-radius: 12px;
  padding: 1rem;
}

.hr-home-payment-title {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.hr-home-payment-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.hr-home-payment-option {
  background: #f5f5f5;
  padding: 0.6rem;
  border-radius: 8px;
  font-size: 0.85rem;
  text-align: center;
  color: #333;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.hr-home-payment-option:hover {
  background: #4169ff;
  color: white;
  transform: translateY(-2px);
}

.hr-home-founder-badge {
  position: absolute;
  right: 20px;
  bottom: 30px;
  background: #4169ff;
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  z-index: 4;
  box-shadow: 0 8px 20px rgba(65, 105, 255, 0.3);
  animation: hr-home-slide-in-up 0.8s ease-out;
}

.hr-home-founder-title {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 1px;
  opacity: 0.9;
  margin-bottom: 0.25rem;
}

.hr-home-founder-name {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  font-style: italic;
}

.hr-home-action-bar {
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1400px;
  width: 100%;
  animation: hr-home-fade-in 0.8s ease;
}

.hr-home-action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #4169ff;
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hr-home-action-btn:hover {
  background: #2d4ed8;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(65, 105, 255, 0.3);
}

.hr-home-dots-indicator {
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
  justify-content: center;
}

.hr-home-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ccc;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hr-home-dot-active {
  background: #4169ff;
  width: 32px;
  border-radius: 6px;
}

.hr-home-dot:hover {
  background: #999;
  transform: scale(1.2);
}

/* Animations */
@keyframes hr-home-slide-in {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes hr-home-slide-in-right {
  from {
    opacity: 0;
    transform: translateX(50px) translateY(-50%);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(-50%);
  }
}

@keyframes hr-home-slide-in-left {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes hr-home-slide-in-up {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes hr-home-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes hr-home-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes hr-home-float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes hr-home-bounce-subtle {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(-3px);
  }
}

@keyframes hr-home-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-slide-in {
  animation: hr-home-slide-in 0.8s ease 0.2s forwards;
}

.animate-slide-in-delay {
  animation: hr-home-slide-in 0.8s ease 0.4s forwards;
}

.animate-fade-in {
  animation: hr-home-fade-in 0.8s ease 0.6s forwards;
}

.animate-float {
  animation: hr-home-float 3s ease-in-out infinite;
}

.animate-bounce-subtle {
  animation: hr-home-bounce-subtle 2s ease-in-out infinite;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .hr-home-content-area {
    gap: 2rem;
  }
  
  .hr-home-main-title {
    font-size: 3rem;
  }
  
  .hr-home-subtitle {
    font-size: 2.5rem;
  }
}

@media (max-width: 1024px) {
  .hr-home-content-area {
    flex-direction: column;
    text-align: center;
  }
  
  .hr-home-right-section {
    width: 100%;
    margin-top: 2rem;
    min-height: 400px;
  }
  
  .hr-home-main-title {
    font-size: 2.5rem;
  }
  
  .hr-home-subtitle {
    font-size: 2rem;
  }
  
  .hr-home-founder-image {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    margin: 0 auto;
  }
  
  .hr-home-product-card {
    position: relative;
    right: auto;
    top: auto;
    margin: 2rem auto 0;
  }
  
  .hr-home-founder-badge {
    position: relative;
    right: auto;
    bottom: auto;
    margin: 2rem auto 0;
    display: inline-block;
  }
  
  .hr-home-blue-shape {
    display: none;
  }
}

@media (max-width: 768px) {
  .hr-home-container {
    padding: 2rem 1.5rem;
    border-radius: 16px;
  }
  
  .hr-home-nav-btn {
    width: 40px;
    height: 40px;
  }
  
  .hr-home-nav-left {
    left: -20px;
  }
  
  .hr-home-nav-right {
    right: -20px;
  }
  
  .hr-home-main-title {
    font-size: 2rem;
  }
  
  .hr-home-subtitle {
    font-size: 1.5rem;
  }
  
  .hr-home-features {
    font-size: 1rem;
  }
  
  .hr-home-button-group {
    flex-direction: column;
    align-items: center;
  }
  
  .hr-home-btn-primary,
  .hr-home-btn-secondary {
    width: 100%;
    max-width: 250px;
    justify-content: center;
  }
  
  .hr-home-product-card {
    width: 100%;
    max-width: 300px;
  }
  
  .hr-home-action-bar {
    flex-direction: column;
    align-items: center;
  }
  
  .hr-home-action-btn {
    width: 100%;
    max-width: 250px;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .hr-home-carousel {
    padding: 1rem;
  }
  
  .hr-home-container {
    padding: 1.5rem 1rem;
  }
  
  .hr-home-main-title {
    font-size: 1.75rem;
  }
  
  .hr-home-subtitle {
    font-size: 1.25rem;
  }
  
  .hr-home-founder-image {
    width: 240px;
    height: 320px;
  }
  
  .hr-home-product-card {
    padding: 1rem;
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}